import { useCallback, useEffect, useState } from 'react';
import { useAuth } from './auth';
import { config } from './config';

export class ApiError extends Error {
  constructor(
    readonly status: number,
    message: string,
  ) {
    super(message);
  }
}

/**
 * Reads retry while the service wakes up: dev and staging pause their database after an hour idle, and the
 * first request can outlast Front Door's wait (a network error or 502/503/504). Writes are never retried.
 */
const WAKE_DELAYS_MS = [3_000, 8_000, 15_000];
const waking = (status: number) => status === 502 || status === 503 || status === 504;
const sleep = (ms: number) => new Promise((r) => setTimeout(r, ms));

/** What the reader is told for a failed request, the same words for every call (403, 401, else the service's message). */
const failureMessage = (status: number, body: { message?: string }) =>
  status === 403 ? 'This is outside your access.' : status === 401 ? 'Please sign in again.' : body.message ?? 'Something went wrong. Please try again.';

/** One request, retried while the service wakes (GET only); returns the last response, or throws when it never came. */
async function sendWithWake(send: () => Promise<Response>, retry: boolean): Promise<Response> {
  for (let attempt = 0; ; attempt++) {
    const again = retry && attempt < WAKE_DELAYS_MS.length;
    try {
      const res = await send();
      if (!again || !waking(res.status)) return res;
    } catch (e) {
      if (!again) throw e;
    }
    await sleep(WAKE_DELAYS_MS[attempt]!);
  }
}

export function useApiClient() {
  const { getToken } = useAuth();
  return useCallback(
    async <T,>(path: string, init?: { method?: string; body?: unknown }): Promise<T> => {
      const token = await getToken();
      const method = init?.method ?? 'GET';
      const send = () =>
        fetch(`${config.apiBase}/api${path}`, {
          method,
          headers: { Authorization: `Bearer ${token}`, ...(init?.body !== undefined ? { 'Content-Type': 'application/json' } : {}) },
          body: init?.body !== undefined ? JSON.stringify(init.body) : undefined,
        });
      const res = await sendWithWake(send, method === 'GET');
      const body = (await res.json().catch(() => ({}))) as { message?: string };
      if (!res.ok) throw new ApiError(res.status, failureMessage(res.status, body));
      return body as T;
    },
    [getToken],
  );
}

/**
 * Fetches an authenticated file and saves it under the name given. Retries while the service wakes, like every GET;
 * a failure throws an ApiError in the portal's words (CP-06), never a silent nothing.
 */
function useFetchFile() {
  const { getToken } = useAuth();
  return useCallback(
    async (path: string, fileName: string) => {
      const token = await getToken();
      let res: Response;
      try {
        res = await sendWithWake(() => fetch(`${config.apiBase}/api${path}`, { headers: { Authorization: `Bearer ${token}` } }), true);
      } catch {
        throw new ApiError(0, 'Could not reach the portal service.');
      }
      if (!res.ok) throw new ApiError(res.status, failureMessage(res.status, (await res.json().catch(() => ({}))) as { message?: string }));
      const url = URL.createObjectURL(await res.blob());
      const link = document.createElement('a');
      link.href = url;
      link.download = fileName;
      link.click();
      setTimeout(() => URL.revokeObjectURL(url), 10_000);
    },
    [getToken],
  );
}

/** A download with its state, for a button that shows it is working and says why it failed (CP-06). */
export function useDownloadState() {
  const fetchFile = useFetchFile();
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<ApiError | null>(null);
  const download = useCallback(
    async (path: string, fileName: string) => {
      setBusy(true);
      setError(null);
      try {
        await fetchFile(path, fileName);
      } catch (e) {
        setError(e instanceof ApiError ? e : new ApiError(0, 'Could not reach the portal service.'));
      } finally {
        setBusy(false);
      }
    },
    [fetchFile],
  );
  /** CP-08 (5E pre-gate): forget an earlier failure, when what it was about is no longer on screen. */
  const clear = useCallback(() => setError(null), []);
  return { download, busy, error, clear };
}

/** Downloads an evidence file (an authenticated binary response) and saves it under its own name; a failure is said in an alert. */
export function useDownload() {
  const fetchFile = useFetchFile();
  return useCallback(
    async (path: string, fileName: string) => {
      try {
        await fetchFile(path, fileName);
      } catch (e) {
        window.alert(e instanceof ApiError ? e.message : 'The file could not be downloaded.');
      }
    },
    [fetchFile],
  );
}

export interface Loadable<T> {
  data: T | null;
  error: ApiError | null;
  loading: boolean;
  reload(): void;
}

export function useApi<T>(path: string | null): Loadable<T> {
  const call = useApiClient();
  const [nonce, setNonce] = useState(0);
  const key = `${path}#${nonce}`;
  // Loading is derived: the stored result belongs to an earlier request until the new one settles.
  const [result, setResult] = useState<{ key: string; data: T | null; error: ApiError | null } | null>(null);
  useEffect(() => {
    if (!path) return;
    let live = true;
    call<T>(path)
      .then((data) => live && setResult({ key, data, error: null }))
      .catch((e: unknown) => live && setResult({ key, data: null, error: e instanceof ApiError ? e : new ApiError(0, 'Could not reach the portal service.') }));
    return () => {
      live = false;
    };
  }, [call, path, key]);
  const current = result?.key === key;
  return {
    data: result?.data ?? null,
    error: current ? (result?.error ?? null) : null,
    loading: !current,
    reload: () => setNonce((n) => n + 1),
  };
}
